import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs';

import {
  authorizationErrorSummary,
  authorizationResponse,
  authorizationUrl,
  AuthEvent,
  AuthorizationErrorSummary,
  AuthTelemetryReporter,
  createCodeChallenge,
  createCodeVerifier,
  createNonce,
  rememberPendingAuthorization,
} from '@ninety/mobile/platform/auth';

import { capacitorAppActions } from '../../capacitor/app/capacitor-app.actions';

import { AuthFailureError, classifyFailure } from './auth-failure';
import { AuthorizationServer } from './authorization-server.service';
import { CommandRunner } from './command-runner.service';
import { ExternalAgent } from './external-agent.service';
import { SessionActions } from './session.actions';
import { AUTH_CLOCK } from './session.effects';
import { selectSession } from './session.selectors';

/**
 * The browser leg of a federated Grant: out to an external user-agent, and back on a custom scheme.
 *
 * `SessionGrantEffects`' sibling. Both end in an ID Token that `SessionTokensEffects` exchanges, and
 * they differ in what authenticates the person — a password we transmit nothing of, or an identity
 * provider we never see the credentials for.
 *
 * ## What the machine already decided, and this does not
 *
 * Nothing here validates the callback. `transition()` compares the returned `state` against the
 * nonce held on the Grant and refuses a response older than five minutes, because the value to
 * compare against lives on the state and nowhere else. This file's job is narrower: generate the
 * material, open the agent, convert one deep link into one event, and redeem the code.
 *
 * ## Callback URLs never reach a log
 *
 * A callback carries an authorization code, so the URL is turned into `URLSearchParams` here and the
 * string itself is never held or logged. It does travel one hop as `appUrlOpened`'s payload, because
 * the URL is the whole of what the plugin event carries and `CapacitorAppEffects` has no business
 * knowing which scheme is a callback. Nothing subscribes to every action except the store timeline,
 * which ships only in dev builds.
 *
 * A callback that *failed* is summarised, through {@link authorizationErrorSummary}: an allow-list of
 * the provider's error code, its own description, and two booleans for whether the credentials were
 * present. Never a deny-list, which would ship any parameter Cognito adds later by default.
 */
@Injectable()
export class SessionAuthorizationEffects {
  private readonly actions$ = inject(Actions);
  private readonly commands = inject(CommandRunner);
  private readonly agent = inject(ExternalAgent);
  private readonly authorizationServer = inject(AuthorizationServer);
  private readonly store = inject(Store);
  private readonly reporter = inject(AuthTelemetryReporter);
  private readonly clock = inject(AUTH_CLOCK);

  /**
   * Generate the material and build the request. Open nothing.
   *
   * Both values are created here, inside the handler, which is the whole reason this Command carries
   * only a provider: a component that could supply a verifier could supply a predictable one, and a
   * component that could supply the nonce could supply the one an attacker already knows.
   *
   * The event reports them to the machine because the Grant is where they have to live — the nonce
   * to check the response against, and the verifier to redeem the code with.
   *
   * **The agent is opened by {@link openExternalAgent$}, from the state this event produces.** Doing
   * both here is what left a callback with nowhere to land; see the `openExternalAgent` Command for
   * the window and why it is wide enough to lose ordinary sign-ins.
   *
   * **Microsoft gets a nonce and no verifier**, because it is not this app that redeems its code —
   * the Resource Server does, at Azure, and its `/Login` contract has no field that could carry one.
   * Generating one anyway and dropping it would read as PKCE to anyone skimming this. It is not, and
   * the `state` nonce below is what that leg does get: CSRF protection and the freshness bound,
   * without the interception protection.
   */
  readonly prepareAuthorizationRequest$ = createEffect(() =>
    this.commands.on('prepareAuthorizationRequest', async ({ provider }): Promise<AuthEvent> => {
      const codeVerifier = provider === 'microsoft' ? undefined : createCodeVerifier();
      const nonce = createNonce();
      const issuedAt = this.clock();

      try {
        const codeChallenge = codeVerifier === undefined ? undefined : await codeChallengeFor(codeVerifier);
        // In this Command rather than beside the open, which is what makes the web ordering
        // structural: the tab is navigated away by a strictly later Command, so the Grant cannot fail
        // to be stored before the redirect that needs it. A no-op on native, where the Grant is never
        // interrupted by a page load.
        rememberPendingAuthorization({ provider, codeVerifier, nonce, issuedAt });
        const url = authorizationUrl(provider, { codeChallenge, state: nonce });
        return { type: 'authorizationRequestPrepared', provider, codeVerifier, nonce, url };
      } catch (error) {
        // `classifyFailure` rethrows what it does not recognise, so only a classified failure ends
        // the Grant here — a provider with no Cognito federation is a bug and stays one.
        return { type: 'grantFailed', cause: classifyFailure(error) };
      }
    })
  );

  /**
   * Put the prepared request in front of the person.
   *
   * Issued by the transition into `awaitingAuthorizationResponse`, so the state already holds the
   * nonce before this runs. That is the fix: a callback arriving while `open` is still in flight —
   * which `@capacitor/browser` makes ordinary, since it resolves from the presentation completion
   * handler — now lands in a state that accepts it.
   *
   * {@link CommandRunner.attempt} rather than `.on`, because only the failure is worth reporting:
   * on web this never returns at all, and on native a success event would arrive *after* the callback
   * on exactly the fast sign-ins this exists for.
   */
  readonly openExternalAgent$ = createEffect(() =>
    this.commands.attempt('openExternalAgent', async ({ url }): Promise<AuthEvent | null> => {
      try {
        await this.agent.open(url);
        return null;
      } catch (error) {
        return { type: 'grantFailed', cause: classifyFailure(error) };
      }
    })
  );

  /**
   * Redeem the code for an ID Token, which then flows into the Token Exchange.
   *
   * A failure here is `grantFailed` and never `refreshFailed`, which is the difference that keeps a
   * refused code from clearing a Vault that may hold a good Durable Root — a Grant is attempted
   * *from* the sign-in screen, including on a device whose cold-start restore was refused for
   * anything but the network.
   */
  readonly requestTokens$ = createEffect(() =>
    this.commands.on('requestTokens', async ({ code, codeVerifier }): Promise<AuthEvent> => {
      try {
        const idToken = await this.authorizationServer.redeemAuthorizationCode(code, codeVerifier);
        return { type: 'idTokenObtained', idToken };
      } catch (error) {
        return { type: 'grantFailed', cause: classifyFailure(error) };
      }
    })
  );

  /** Teardown, so there is no outcome for the machine to decide anything about. */
  readonly closeExternalAgent$ = createEffect(() =>
    this.commands.teardown('closeExternalAgent', () => this.agent.close())
  );

  /**
   * One deep link becomes one authorization response.
   *
   * Capacitor delivers the deep link outside Angular's zone, which costs no repaint: since v18 Angular
   * schedules change detection from `markForCheck`, signal writes and view attachment rather than from
   * the zone's microtask queue, and v21 removed the `ignoreChangesOutsideZone` opt-out — so the
   * selectors this drives repaint either way.
   */
  readonly authorizationResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(capacitorAppActions.appUrlOpened),
      map(({ url }) => authorizationResponse(url)),
      filter((params): params is URLSearchParams => params !== null),
      map(params => SessionActions.eventReceived({ event: { type: 'authorizationResponseReceived', params } }))
    )
  );

  /**
   * Say what a failed callback reported, whichever platform it arrived on.
   *
   * Driven off the dispatched event rather than the listener, so the deep link on native and the
   * resumed redirect on web are covered by one rule — both events carry `params`, and neither keeps
   * anything else from the callback. Reported here rather than derived from a transition because what
   * the provider said is a property of the callback: `transition()` routes every error to
   * `authorizationAbandoned` and keeps none of the detail.
   */
  readonly reportCallbackError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.eventReceived),
        map(({ event }) => ('params' in event ? authorizationErrorSummary(event.params) : null)),
        filter((summary): summary is AuthorizationErrorSummary => summary !== null),
        tap(summary => this.reporter.authorizationError(summary))
      ),
    { dispatch: false }
  );

  /**
   * The person closed the agent without finishing.
   *
   * **The one dispatch site here that reads the state first**, against the rule that guards belong in
   * `transition()`. The rule is not being re-derived: the OS callback is the same whether someone
   * dismissed the agent or we closed it ourselves from `closeExternalAgent`, so what it *means*
   * depends on whether a response was still awaited. Dispatching unconditionally would put a
   * guaranteed illegal pair on every completed federated sign-in, and `auth.illegal_transition` is
   * the signal that tells us which races are real — it earns that by not carrying entries we
   * manufactured.
   */
  readonly agentDismissed$ = createEffect(() =>
    this.agent.dismissed$.pipe(
      concatLatestFrom(() => this.store.select(selectSession)),
      filter(([, state]) => state.tag === 'authorizing' && state.grant.kind === 'awaitingAuthorizationResponse'),
      map(() => SessionActions.eventReceived({ event: { type: 'authorizationAbandoned' } }))
    )
  );
}

/**
 * The S256 challenge, as a classified failure when this context cannot compute one.
 *
 * The sibling of the wrap inside {@link ExternalAgent.open}, and here for the same reason: the two
 * things that can fail before the agent opens both have to end the Grant, or the machine parks in
 * `preparingAuthorization` behind a spinner covering the password form that is the only way in.
 * `createCodeChallenge` throws a bare `TypeError` when `crypto.subtle` is absent — which is every
 * attempt under Capacitor live reload, whose `server.url` serves a plain-HTTP origin — and
 * `classifyFailure` rethrows what it does not recognise, so unwrapped it escaped as
 * `auth.command_failed` and dispatched nothing.
 *
 * Deliberately narrow: only the digest is wrapped, so a genuine bug anywhere else in the handler
 * still escapes as one.
 */
async function codeChallengeFor(verifier: string): Promise<string> {
  try {
    return await createCodeChallenge(verifier);
  } catch {
    throw new AuthFailureError(
      { kind: 'authorizationFailed', detail: 'pkceUnavailable' },
      'an S256 code challenge could not be computed in this context'
    );
  }
}
